import React, { useEffect, useState, useRef } from 'react';
import Modal from 'utils/components/modal';
import Switch from '../../common/inputs/customs/switch';
import FAQ from '../../common/faqs/conversation-header';
import TitleAndDescription, { HEADER_FONTS } from '../../common/description-component';
import SelectInput from '../../common/inputs/select-input';
import TextField from '../../common/inputs/text-field';
import EmbedCode from '../../common/embed-code';
import useSpotConfig from '../../../clients/request/config/use-config';

import {
  spotlightConfigurations,
  SPOTLIGHT_ENABLE,
  SPOTLIGHT_IS_SIDEBAR,
  SPOTLIGHT_TYPE,
  SPOTLIGHT_URL
} from './spotlight-consts';

const MESSAGE_TIME_TO_SHOW = 7000;

export const SPOTLIGHT_LOCATIONS = [
  { value: 'above', text: 'Above the conversation' },
  { value: 'custom', text: 'Custom location' },
  { value: 'sidebar', text: 'Sidebar' }
];

export const SPOTLIGHT_TYPES = [
  { value: 'form', text: 'Email collector' },
  { value: 'counter', text: 'Counter' },
  { value: 'redirect', text: 'Redirect' }
];

const SpotlightEmbedCode = ({ isSidebar }) => (
  <div>
    <EmbedCode
      type="spotlight"
      isSidebar={isSidebar}
      title="Embed Code"
      description="Embed this code where you'd like Spotlight to load on the page."
    />
  </div>
);

const CommunitySpotlight = ({ spot }) => {
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [backupCustomUrl, setBackupCustomUrl] = useState('');
  const [convHeaderData, setConvHeaderData] = useState({
    enabled: false,
    customUrl: '',
    type: 'form',
    isSidebar: false,
    isCustomLocation: false
  });

  const [configValues, setConfig, { loading, errorFetching, errorSaving, saving }] = useSpotConfig(
    spotlightConfigurations
  );

  //spotlight module doesn't really have redirect type but, counter + customUrl is treated as redirect
  const formatType = type => (type === 'redirect' ? 'counter' : type);

  useEffect(() => {
    if (loading) {
      return;
    }
    const [enabled, type, customUrl, isSidebar = false] = configValues;

    const newConvHeaderData = {
      ...convHeaderData,
      ...({
        customUrl,
        type: formatType(type),
        isSidebar,
        enabled
      } as typeof convHeaderData)
    };

    setConvHeaderData(newConvHeaderData);
  }, [configValues, loading]);

  useEffect(() => {
    if (saving) {
      setIsSaving(true);
    } else if (errorSaving) {
      setErrorMessage('Failed to save. Please try again later.');
      setMessage('');
      setIsSaving(false);
      clearMessages();
    } else if (isSaving && !errorSaving) {
      setMessage('Settings was successfully saved.');
      setErrorMessage('');
      setIsSaving(false);
      clearMessages();
    }

    if (errorFetching) {
      setErrorMessage('Failed to fetch Spotlight data. Please try again later.');
      clearMessages();
    }
  }, [saving, errorSaving]);

  const clearMessages = () => {
    setTimeout(() => {
      setMessage('');
      setErrorMessage('');
    }, MESSAGE_TIME_TO_SHOW);
  };

  const toggleConvHeader = async () => {
    setConvHeaderData({ ...convHeaderData, enabled: !convHeaderData.enabled });
  };

  const handleCustomUrlChange = value => {
    const newConvHeaderData = { ...convHeaderData, customUrl: value || '' };
    setConvHeaderData(newConvHeaderData);
  };

  const handleTypeChange = value => {
    const newConvHeaderData = { ...convHeaderData, type: value };

    if (value !== 'redirect') {
      setBackupCustomUrl(newConvHeaderData.customUrl);
      newConvHeaderData.customUrl = '';
    } else {
      newConvHeaderData.customUrl = backupCustomUrl;
    }

    setConvHeaderData(newConvHeaderData);
  };

  const handleCustomLocationChange = value => {
    const isCustomLocation = value === 'custom';
    const isSidebar = value === 'sidebar';
    const newConvHeaderData = { ...convHeaderData, isCustomLocation, isSidebar };

    setConvHeaderData(newConvHeaderData);
  };

  const saveSettings = async () => {
    const { enabled, type, customUrl, isSidebar } = convHeaderData;

    const formattedType = formatType(type);

    const setConfigPayload = [
      { key: SPOTLIGHT_ENABLE, value: enabled, active: true },
      { key: SPOTLIGHT_TYPE, value: formattedType, active: true },
      { key: SPOTLIGHT_URL, value: customUrl || false, active: true },
      { key: SPOTLIGHT_IS_SIDEBAR, value: isSidebar, active: true }
    ];
    setConfig(setConfigPayload, spot.name);
  };

  const renderTitleAndDescription = () => {
    return (
      <React.Fragment>
        <TitleAndDescription
          titleFont={HEADER_FONTS.Large}
          title="Spotlight"
          description={
            <p>
              The <b>Community Spotlight</b> feature will help you showcase and motivate your
              community while creating additional revenue opportunities. With fresh insights and a
              call-to-action, your community will be more prominently prompted to participate in the
              conversation and join the community as registered users. Overall choosing to use this
              feature will boost your number of returning power users. Overall enabling this feature
              will drive <b>more user participation</b> and <b>more revenue</b>.
            </p>
          }
        />

        <div className="image-container">
          <img src="https://publisher-assets.spot.im/host-panel/Conversation_header_img.png" />
        </div>
      </React.Fragment>
    );
  };

  const { enabled, customUrl, type, isCustomLocation = false, isSidebar = false } = convHeaderData;
  const isRedirect = type === 'redirect';
  const showEmbedCode = isCustomLocation || isSidebar;
  const isSubmitDisabled = isRedirect && !customUrl;

  return loading ? (
    <div className="loading" />
  ) : (
    <div className="conversationHeaders">
      {message && (
        <div className="success-message">
          <div className="text">{message}</div>
        </div>
      )}
      {errorMessage && (
        <div className="error-message">
          <div className="text">{errorMessage}</div>
        </div>
      )}
      <div className="main-section">
        <div className="embed-section">
          {renderTitleAndDescription()}
          <Switch
            title="Enable/Disable Spotlight"
            className="enable"
            value={enabled || false}
            onChange={toggleConvHeader}
          />
          {Object.keys(convHeaderData).length > 0 && (
            <React.Fragment>
              {!!enabled && (
                <div>
                  <SelectInput
                    description="Choose Card Type"
                    onChange={option => handleTypeChange(option.value)}
                    options={SPOTLIGHT_TYPES}
                    value={SPOTLIGHT_TYPES.find(option =>
                      customUrl ? option.value === 'redirect' : option.value === type
                    )}
                  />

                  {(customUrl || isRedirect) && (
                    <TextField
                      type="text"
                      className="outside"
                      onChange={handleCustomUrlChange}
                      title="Redirect URL"
                      value={customUrl || ''}
                      placeholder=""
                      maxLength={60}
                    />
                  )}
                  <SelectInput
                    description="Choose Location"
                    onChange={option => handleCustomLocationChange(option.value)}
                    options={SPOTLIGHT_LOCATIONS}
                    value={SPOTLIGHT_LOCATIONS.find(
                      option =>
                        (isCustomLocation && option.value === 'custom') ||
                        (isSidebar && option.value === 'sidebar') ||
                        (!isCustomLocation && !isSidebar && option.value === 'above')
                    )}
                  />

                  {showEmbedCode && <SpotlightEmbedCode isSidebar={isSidebar} />}
                </div>
              )}
            </React.Fragment>
          )}

          <button onClick={saveSettings} disabled={isSubmitDisabled} className="policy-button">
            Save & Publish
          </button>
        </div>
      </div>
      <FAQ />
    </div>
  );
};

export default CommunitySpotlight;
